import type { AccessContext } from "./access"
import { scoreDestination } from "./ranking"
import type {
  Destination,
  DestinationMatch,
  ModuleKey,
  ResultGroup,
} from "./types"

/**
 * Whether a destination may be shown at all, before any matching.
 *
 * Unpermitted destinations are omitted entirely — never rendered greyed out,
 * never counted in a group header. Absence of permission has to be
 * indistinguishable from absence of data, or search becomes an org-structure
 * oracle (README §3).
 */
export function isVisible(
  destination: Destination,
  access: AccessContext
): boolean {
  if (
    destination.shells !== undefined &&
    !destination.shells.includes(access.shell)
  ) {
    return false
  }

  // ALL of the required modules, deliberately unlike permissions. A destination
  // needing both HRIS and Payroll needs both switches on; a destination listing
  // three permissions needs only one of them.
  if (
    destination.requiresModule !== undefined &&
    !destination.requiresModule.every(access.moduleOn)
  ) {
    return false
  }

  // `ungated` is a registry-level statement that a route carries no gate in the
  // router either (SCOPE.md D3). access.canAny([]) is false by design, so
  // without this branch an ungated entry would be invisible to everyone.
  if (destination.ungated !== undefined) return true

  return access.canAny(destination.permissions)
}

/**
 * Ordering is fully determined: score descending, then label, then id. Nothing
 * falls back on array order, so results cannot reshuffle between keystrokes
 * that produce the same scores.
 */
const byScoreThenLabel = (a: DestinationMatch, b: DestinationMatch) =>
  b.score - a.score ||
  a.destination.label.localeCompare(b.destination.label) ||
  a.destination.id.localeCompare(b.destination.id)

export interface SearchParams {
  query: string
  destinations: Destination[]
  access: AccessContext
  /** Module the user is currently viewing, for the relevance nudge. */
  currentModule?: ModuleKey
  /** Cap on total matches, applied after ranking. */
  limit?: number
}

/** Flat, ranked matches. Grouping is a presentation step on top of this. */
export function searchDestinations({
  query,
  destinations,
  access,
  currentModule,
  limit,
}: SearchParams): DestinationMatch[] {
  const matches = destinations
    .filter((destination) => isVisible(destination, access))
    .map((destination) => ({
      destination,
      score: scoreDestination(destination, query, currentModule),
    }))
    .filter((match) => match.score > 0)
    .sort(byScoreThenLabel)

  return limit === undefined ? matches : matches.slice(0, limit)
}

/**
 * Groups ranked matches by module. Group order follows each group's best match,
 * so the most relevant module heads the list; ties fall back to the module key
 * for stability. Empty groups are never emitted.
 */
export function groupByModule(matches: DestinationMatch[]): ResultGroup[] {
  const groups = new Map<ModuleKey, DestinationMatch[]>()

  for (const match of matches) {
    const existing = groups.get(match.destination.module)
    if (existing) existing.push(match)
    else groups.set(match.destination.module, [match])
  }

  return Array.from(groups, ([module, groupMatches]) => ({
    module,
    matches: groupMatches,
  })).sort(
    (a, b) =>
      b.matches[0].score - a.matches[0].score ||
      a.module.localeCompare(b.module)
  )
}

/**
 * The module the user is currently in, for the relevance nudge. Picks the
 * destination whose route is the longest prefix of the current path, so
 * `/dashboard/payroll/payrollrecord/view?x=1` resolves through
 * `/dashboard/payroll/payrollrecord` rather than stopping at `/dashboard/payroll`.
 */
export function currentModuleFromPath(
  pathname: string,
  candidates: Destination[]
): ModuleKey | undefined {
  let best: Destination | undefined

  for (const destination of candidates) {
    const matches =
      pathname === destination.route ||
      pathname.startsWith(`${destination.route}/`)

    if (matches && (!best || destination.route.length > best.route.length)) {
      best = destination
    }
  }

  return best?.module
}
